/**
 * Detailed Report Component
 * Displays advanced metrics and demographic breakdowns for ENTERPRISE tier subscribers
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import apiService from '../../../lib/services/apiService';
import { transformSurveyWave } from '../../../lib/utils/filmMetricsUtils';
import { getMetricAtWeek, calculateWeeksBeforeRelease } from '../../../lib/services/compReportService';

const DetailedReport = ({ primaryFilmId, primaryFilmTitle, compFilms, reportData, loading, error }) => {
  const [demographicTab, setDemographicTab] = useState('agegroup');
  const [demographicData, setDemographicData] = useState({});
  const [loadingDemographics, setLoadingDemographics] = useState(false);
  const [demographicError, setDemographicError] = useState(null);

  // Advanced metrics to display
  const advancedMetrics = [
    { key: 'pctAware', label: 'Awareness', unit: '%' },
    { key: 'pctInterest', label: 'Interest', unit: '%' },
    { key: 'pctInterestOfAware', label: 'Interest of Aware', unit: '%' },
    { key: 'pctInterestNotAware', label: 'Interest Not Aware', unit: '%' },
    { key: 'pctTheater', label: 'Theater Intent', unit: '%' },
    { key: 'pctWillingToPay', label: 'Willing to Pay', unit: '%' },
    { key: 'pctHeat', label: 'Heat', unit: '%' },
    { key: 'pctPLF', label: 'PLF', unit: '%' }
  ];

  // Demographic options
  const demographics = [
    { value: 'agegroup', label: 'Age Group' },
    { value: 'gender', label: 'Gender' },
    { value: 'ethnicity', label: 'Ethnicity' },
    { value: 'movie_frequency_category', label: 'Movie Frequency' }
  ];

  // Fetch demographic data for a specific film and week
  const fetchDemographicDataForFilm = async (imdbId, weeksBeforeRelease) => {
    try {
      // Use getFilmSurveyToplinesAggregated for demographic breakdowns (same as Deep Dive)
      // This method properly handles group_by with demographic fields
      const response = await apiService.getFilmSurveyToplinesAggregated({
        aggregate: 'true',
        group_by: `imdb_id,FilmRecencyOrder,title_text,SurveyCompleteDate,releasedate,${demographicTab}`,
        imdb_id: imdbId
      });

      if (!response.success || !Array.isArray(response.data)) {
        return {};
      }

      // Group by demographic value and weeks before release
      const grouped = {};
      
      response.data.forEach(record => {
        const demoValue = record[demographicTab] || 'Unknown';
        const transformed = transformSurveyWave(record);
        const weeks = calculateWeeksBeforeRelease(
          transformed.surveyCompleteDate || transformed.surveyCompleteDateRaw,
          transformed.releaseDate || transformed.releaseDateRaw
        );
        
        if (weeks === weeksBeforeRelease) {
          if (!grouped[demoValue]) {
            grouped[demoValue] = [];
          }
          grouped[demoValue].push(transformed);
        }
      });

      // For each demographic group, use the most recent wave
      const result = {};
      Object.keys(grouped).forEach(demoValue => {
        const waves = grouped[demoValue];
        const mostRecent = waves.sort((a, b) => 
          (a.FilmRecencyOrder || 999) - (b.FilmRecencyOrder || 999)
        )[0];
        result[demoValue] = mostRecent;
      });

      return result;
    } catch (err) {
      console.error('Failed to fetch demographic data:', err);
      return {};
    }
  };

  // Fetch demographic data when tab or report data changes
  useEffect(() => {
    if (!reportData || !reportData.weeks || reportData.weeks.length === 0) return;

    const fetchAllDemographics = async () => {
      setLoadingDemographics(true);
      setDemographicError(null);

      try {
        const allData = {};
        
        // Fetch for primary film
        for (const week of reportData.weeks) {
          const primaryData = await fetchDemographicDataForFilm(primaryFilmId, week);
          if (!allData[week]) allData[week] = {};
          if (!allData[week].primary) allData[week].primary = {};
          allData[week].primary = primaryData;
        }

        // Fetch for comp films
        for (const comp of compFilms) {
          for (const week of reportData.weeks) {
            const compData = await fetchDemographicDataForFilm(comp.compFilmId, week);
            if (!allData[week]) allData[week] = {};
            if (!allData[week][comp.compFilmId]) allData[week][comp.compFilmId] = {};
            allData[week][comp.compFilmId] = compData;
          }
        }

        setDemographicData(allData);
      } catch (err) {
        setDemographicError(err.message);
      } finally {
        setLoadingDemographics(false);
      }
    };

    fetchAllDemographics();
  }, [demographicTab, reportData, primaryFilmId, compFilms]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error.message || 'Failed to load report data'}
      </Alert>
    );
  }

  if (!reportData || !reportData.primaryFilm || !reportData.weeks || reportData.weeks.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No data available for comparison. Ensure the primary film and comp films have survey data.
        </Typography>
      </Paper>
    );
  }

  const { primaryFilm, compFilms: comps, weeks } = reportData;

  // Get color for metric value
  const getMetricColor = (value) => {
    if (value === 'N/A' || typeof value !== 'number') return 'text.secondary';
    if (value >= 50) return 'success.main';
    if (value >= 30) return 'warning.main';
    return 'error.main';
  };

  // Get unique demographic values for current tab
  const getDemographicValues = (week) => {
    const values = new Set();
    if (demographicData[week]?.primary) {
      Object.keys(demographicData[week].primary).forEach(v => values.add(v));
    }
    comps.forEach(comp => {
      if (demographicData[week]?.[comp.imdbId]) {
        Object.keys(demographicData[week][comp.imdbId]).forEach(v => values.add(v));
      }
    });
    return Array.from(values).sort();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Detailed Comparison Report
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Demographic</InputLabel>
          <Select
            value={demographicTab}
            label="Demographic"
            onChange={(e) => setDemographicTab(e.target.value)}
          >
            {demographics.map(demo => (
              <MenuItem key={demo.value} value={demo.value}>
                {demo.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Comparing {primaryFilmTitle} with {comps.length} comp film{comps.length !== 1 ? 's' : ''} by weeks before release with demographic breakdowns
      </Typography>

      {loadingDemographics && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={30} />
        </Box>
      )}

      {demographicError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Some demographic data could not be loaded: {demographicError}
        </Alert>
      )}

      {/* Overall Metrics (without demographics) */}
      {advancedMetrics.map((metric, metricIndex) => (
        <TableContainer component={Paper} key={metric.key} sx={{ mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {metric.label} - Overall
            </Typography>
          </Box>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 1 }}>
                  Weeks Before Release
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                  {primaryFilm.filmTitle}
                </TableCell>
                {comps.map((comp) => (
                  <TableCell key={comp.imdbId} sx={{ fontWeight: 600 }}>
                    {comp.filmTitle}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {weeks.map((week) => {
                const primaryValue = getMetricAtWeek(primaryFilm, week, metric.key);
                return (
                  <TableRow key={week} hover>
                    <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 1 }}>
                      {week} weeks
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        backgroundColor: 'primary.light',
                        color: typeof primaryValue === 'number' ? getMetricColor(primaryValue) : 'text.secondary',
                        fontWeight: 600
                      }}
                    >
                      {primaryValue === 'N/A' ? (
                        <Chip label="N/A" size="small" variant="outlined" />
                      ) : (
                        `${primaryValue}${metric.unit}`
                      )}
                    </TableCell>
                    {comps.map((comp) => {
                      const compValue = getMetricAtWeek(comp, week, metric.key);
                      return (
                        <TableCell 
                          key={comp.imdbId}
                          sx={{ 
                            color: typeof compValue === 'number' ? getMetricColor(compValue) : 'text.secondary'
                          }}
                        >
                          {compValue === 'N/A' ? (
                            <Chip label="N/A" size="small" variant="outlined" />
                          ) : (
                            `${compValue}${metric.unit}`
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ))}

      {/* Demographic Breakdowns */}
      {weeks.map((week) => {
        const demoValues = getDemographicValues(week);
        if (demoValues.length === 0) return null;

        return (
          <Box key={week} sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {week} Weeks Before Release - {demographics.find(d => d.value === demographicTab)?.label} Breakdown
            </Typography>
            
            {advancedMetrics.map((metric) => (
              <TableContainer component={Paper} key={`${week}-${metric.key}`} sx={{ mb: 2 }}>
                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', backgroundColor: 'grey.50' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {metric.label}
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {demographics.find(d => d.value === demographicTab)?.label}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                        {primaryFilm.filmTitle}
                      </TableCell>
                      {comps.map((comp) => (
                        <TableCell key={comp.imdbId} sx={{ fontWeight: 600 }}>
                          {comp.filmTitle}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {demoValues.map((demoValue) => {
                      const primaryDemoData = demographicData[week]?.primary?.[demoValue];
                      const primaryValue = primaryDemoData?.[metric.key] ?? 'N/A';
                      
                      return (
                        <TableRow key={demoValue} hover>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {demoValue}
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              backgroundColor: 'primary.light',
                              color: typeof primaryValue === 'number' ? getMetricColor(primaryValue) : 'text.secondary'
                            }}
                          >
                            {primaryValue === 'N/A' ? (
                              <Chip label="N/A" size="small" variant="outlined" />
                            ) : (
                              `${primaryValue}${metric.unit}`
                            )}
                          </TableCell>
                          {comps.map((comp) => {
                            const compDemoData = demographicData[week]?.[comp.imdbId]?.[demoValue];
                            const compValue = compDemoData?.[metric.key] ?? 'N/A';
                            
                            return (
                              <TableCell 
                                key={comp.imdbId}
                                sx={{ 
                                  color: typeof compValue === 'number' ? getMetricColor(compValue) : 'text.secondary'
                                }}
                              >
                                {compValue === 'N/A' ? (
                                  <Chip label="N/A" size="small" variant="outlined" />
                                ) : (
                                  `${compValue}${metric.unit}`
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ))}
          </Box>
        );
      })}
    </Box>
  );
};

export default DetailedReport;

